import React, {useEffect, useState} from "react";
import * as XLSX from "xlsx";
import {useTheme} from "@mui/material/styles";
import {
  AppBar,
  Card,
  Grid,
  Paper,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  InputLabel,
  MenuItem,
  Select,
  FormControl,
  Box,
  Tooltip
} from "@mui/material";
import {DataGrid} from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Typography from "@mui/material/Typography";
import {GET} from "APIclient";
import {useNavigate} from "react-router-dom";
import {formatDesignation} from "constants/ApplicationConstants";
import properties from "constants/ApplicationConstants";
import {formatDate} from "constants/ApplicationConstants";
import {LastPage, FirstPage, NavigateBefore, NavigateNext} from "@mui/icons-material";

function CustomPagination({
  page,
  pageSize,
  totalRows,
  totalPages,
  onPageChange,
  onGoToLastPage,
  loading
}) {
  const handleFirstPage = () => {
    onPageChange(0);
  };

  const handlePreviousPage = () => {
    onPageChange(Math.max(0, page - 1));
  };

  const handleNextPage = () => {
    onPageChange(Math.min(totalPages - 1, page + 1));
  };

  const handleLastPage = () => {
    onGoToLastPage();
  };

  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, totalRows);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1,
        borderTop: "1px solid #e0e0e0"
      }}
    >
      <Typography variant="caption" sx={{color: "#666"}}>
        {totalRows > 0 ? `${startRow}-${endRow} of ${totalRows}` : "No rows"}
      </Typography>

      <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
        <Typography variant="caption" sx={{mr: 2, color: "#666"}}>
          Page {page + 1} of {totalPages || 1}
        </Typography>

        <Tooltip title="First Page">
          <IconButton onClick={handleFirstPage} disabled={page === 0 || loading} size="small">
            <FirstPage />
          </IconButton>
        </Tooltip>

        <Tooltip title="Previous Page">
          <IconButton onClick={handlePreviousPage} disabled={page === 0 || loading} size="small">
            <NavigateBefore />
          </IconButton>
        </Tooltip>

        <Tooltip title="Next Page">
          <IconButton
            onClick={handleNextPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
          >
            <NavigateNext />
          </IconButton>
        </Tooltip>

        <Tooltip title="Last Page">
          <IconButton
            onClick={handleLastPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
            sx={{
              backgroundColor: properties?.colors?.bgcolorbluethem || "#1976d2",
              color: "white",
              "&:hover": {
                backgroundColor: properties?.colors?.bgcolorbluethem || "#1565c0"
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e"
              }
            }}
          >
            <LastPage />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

const ReportingList = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [reportingList, setReportingList] = useState([]);
  const [filteredReportingList, setFilteredReportingList] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [searchEmployeeId, setSearchEmployeeId] = useState("");
  const [searchEmployeeName, setSearchEmployeeName] = useState("");
  const [searchReportingManagerId, setSearchReportingManagerId] = useState("");
  const [searchReportingManagerName, setSearchReportingManagerName] = useState("");
  const [searchReportingStatus, setSearchReportingStatus] = useState("");
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackColor, setSnackColor] = useState("success");
  const [optionValue, setOptionValue] = useState(false);
  const [loading, setLoading] = useState(false);
  // const [totalRows, setTotalRows] = useState(0);
  //   const [totalPages, setTotalPages] = useState(0);

  const options = ["Assign Manager"];

  const columns = [
    {field: "id", headerName: "#", width: 30, align: "left", headerAlign: "left"},
    {field: "empId", headerName: "EMP ID", width: 70, align: "left", headerAlign: "left"},
    {
      field: "employeeName",
      headerName: "Employee Name",
      width: 150,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "reportingFrom",
      headerName: "Reporting From",
      width: 130,
      align: "left",
      headerAlign: "left",
      renderCell: params => {
        const formattedDate = formatDate(params.value);
        return formattedDate || " N/A ";
      }
    },
    {
      field: "designation",
      headerName: "Designation",
      width: 200,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "reportingManagerID",
      headerName: "Reporting Manager ID",
      width: 180,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "reportingName",
      headerName: "Reporting Name",
      width: 150,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 80,
      align: "left",
      headerAlign: "left",
      cellClassName: "actions",
      renderCell: params => {
        return (
          <div style={{textAlign: "left"}}>
            <Select
              variant="standard"
              key={`menu-${params.row.empId}`}
              value={optionValue}
              onChange={event => handleViewDetails(params.row, event.target.value)}
              displayEmpty
              defaultValue=""
              style={{width: "1px"}}
              renderValue={selected => {
                if (!selected) {
                  return (
                    <span
                      style={{display: "flex", alignItems: "center", justifyContent: "flex-start"}}
                    >
                      <MoreVertIcon style={{marginRight: "8px", width: ""}} />
                    </span>
                  );
                }
                return selected;
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    textAlign: "left"
                  }
                }
              }}
            >
              {options?.map((option, index) => (
                <MenuItem key={index} value={option} style={{justifyContent: "flex-start"}}>
                  {option ? option : <MoreVertIcon />}
                </MenuItem>
              ))}
            </Select>
          </div>
        );
      }
    }
  ];

  const fetchExperienceReports = async (
    page,
    pageSize,
    empId,
    employeeName,
    reportingManagerId,
    reportingManagerName,
    reportingStatus
  ) => {
    try {
      setLoading(true);
      const offset = page;
      const queryParams = new URLSearchParams({
        offset,
        limit: pageSize,
        empId: empId || "",
        empName: employeeName || "",
        reportingManagerName: reportingManagerName || "",
        reportingManagerID: reportingManagerId || "",
        reportingStatus: reportingStatus || ""
      }).toString();

      const response = await GET(`admin/getEmployeeReportingReports?${queryParams}`);
      if (response.status === 200 && Array.isArray(response.data?.data?.paginatedData)) {
        const data = response.data?.data?.paginatedData.map((item, index) => ({
          id: page * pageSize + index + 1,
          empId: item.empId,
          employeeName: item.employeeName,
          reportingFrom: item.reportingFrom ? item.reportingFrom : "-",
          designation: formatDesignation(item?.designation),
          reportingManagerID: item.reportingManagerID ? item.reportingManagerID : "-",
          reportingName: item.reportingName ? item.reportingName : "-",
          reportingStatus: item.reportingManagerID ? "Set" : "Not Set"
        }));
        const totalRecords = response.data?.data?.totalItems || 0;
        const totalPages = response.data?.data?.totalPages || 0;

        setReportingList(data);
        setFilteredReportingList(data);
        setTotalRows(totalRecords);
        setTotalPages(totalPages);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      setSnackMessage("Failed to fetch experience reports.");
      setSnackColor("error");
      setSnackOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExperienceReports(
      page,
      pageSize,
      searchEmployeeId,
      searchEmployeeName,
      searchReportingManagerId,
      searchReportingManagerName,
      searchReportingStatus
    );
  }, [page, pageSize]);

  const handleViewDetails = (row, selectedOption) => {
    setOptionValue(selectedOption);
    if (selectedOption === "Assign Manager") {
      navigate(`/reporting`, {state: row.empId});
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedReport(null);
  };

  const handleDownloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredReportingList);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Reporting List");
    XLSX.writeFile(workbook, "Reporting_List.xlsx");
  };

  const handleSearch = () => {
    setPage(0);
    fetchExperienceReports(
      0,
      pageSize,
      searchEmployeeId,
      searchEmployeeName,
      searchReportingManagerId,
      searchReportingManagerName,
      searchReportingStatus
    );
  };

  const handleKeyDown = event => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const handlePaginationChange = paginationModel => {
    setPage(paginationModel.page);
    setPageSize(paginationModel.pageSize);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handlePageChange = newPage => {
    setPage(newPage);
    fetchExperienceReports(
      newPage,
      pageSize,
      searchEmployeeId,
      searchEmployeeName,
      searchReportingManagerId,
      searchReportingManagerName,
      searchReportingStatus
    );
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handleGoToLastPage = async () => {
    if (totalPages > 0) {
      const lastPageIndex = totalPages - 1;
      setPage(lastPageIndex);
      await fetchExperienceReports(
        lastPageIndex,
        pageSize,
        searchEmployeeId,
        searchEmployeeName,
        searchReportingManagerId,
        searchReportingManagerName,
        searchReportingStatus
      );
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 0);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static">
          <Card>
            <MDBox pt={2} px={1} pb={2}>
              <MDBox
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(13, 1fr)",
                  gap: 1,
                  alignItems: "center",
                  whiteSpace: "nowrap"
                }}
              >
                <Typography
                  sx={{
                    color: "textPrimary",
                    fontFamily: properties.fontFamily,
                    fontSize: "15px",
                    fontWeight: "bold"
                  }}
                >
                  Reporting List
                </Typography>
                <MDInput
                  sx={{gridColumn: "span 2"}}
                  value={searchEmployeeId}
                  onChange={e => setSearchEmployeeId(e.target.value)}
                  label="Employee ID"
                  onKeyDown={handleKeyDown}
                />

                <MDInput
                  sx={{gridColumn: "span 2"}}
                  value={searchEmployeeName}
                  onChange={e => setSearchEmployeeName(e.target.value)}
                  label="Employee Name"
                  onKeyDown={handleKeyDown}
                />
                <MDInput
                  sx={{gridColumn: "span 2"}}
                  value={searchReportingManagerId}
                  onChange={e => setSearchReportingManagerId(e.target.value)}
                  label="Reporting ID"
                  onKeyDown={handleKeyDown}
                />
                <MDInput
                  sx={{gridColumn: "span 2"}}
                  value={searchReportingManagerName}
                  onChange={e => setSearchReportingManagerName(e.target.value)}
                  label="Reporting Name"
                  onKeyDown={handleKeyDown}
                />
                <FormControl sx={{gridColumn: "span 2"}} fullWidth>
                  <InputLabel id="reporting-status-label">Reporting Status</InputLabel>
                  <Select
                    labelId="reporting-status-label"
                    id="demo-simple-select"
                    value={searchReportingStatus}
                    onChange={e => setSearchReportingStatus(e.target.value)}
                    label="Reporting Status"
                    sx={{
                      height: "43px"
                    }}
                    onKeyDown={handleKeyDown}
                  >
                    <MenuItem value="1">Set</MenuItem>
                    <MenuItem value="0">Not Set</MenuItem>
                  </Select>
                </FormControl>
                <MDButton
                  sx={{gridColumn: "span 1", background: properties.colors.bgcolorbluethem}}
                  variant="gradient"
                  color="info"
                  onClick={handleSearch}
                >
                  Search
                </MDButton>
                <MDButton
                  sx={{gridColumn: "span 1"}}
                  variant="gradient"
                  color="success"
                  onClick={handleDownloadExcel}
                >
                  Download
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        </AppBar>
        <MDBox>
          <Paper>
            <DataGrid
              rows={filteredReportingList}
              columns={columns}
              paginationMode="server"
              rowCount={totalRows}
              pageSizeOptions={[10, 20, 50]}
              paginationModel={{page, pageSize}}
              onPaginationModelChange={handlePaginationChange}
              loading={loading}
              hideFooter
              sx={{
                fontFamily: properties.fontFamily,
                fontSize: "14px",
                "& .MuiDataGrid-cell": {
                  textAlign: "left"
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  textAlign: "left"
                },
                "& .MuiDataGrid-columnHeader": {
                  textAlign: "left"
                },
                "& .MuiDataGrid-footerContainer": {
                  justifyContent: "flex-start"
                },
                height: "450px",
                fontWeight: 400,
                "& .MuiDataGrid-scrollbar": {
                  width: "10px !important"
                }
              }}
            />
            <CustomPagination
              page={page}
              pageSize={pageSize}
              totalRows={totalRows}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onGoToLastPage={handleGoToLastPage}
              loading={loading}
            />
          </Paper>
        </MDBox>
        <Snackbar
          open={snackOpen}
          autoHideDuration={3000}
          onClose={() => setSnackOpen(false)}
          anchorOrigin={{vertical: "top", horizontal: "right"}}
        >
          <Alert onClose={() => setSnackOpen(false)} severity={snackColor}>
            {snackMessage}
          </Alert>
        </Snackbar>
        <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="sm">
          <DialogTitle>
            Employee Reporting List
            <IconButton edge="end" onClick={handleCloseModal}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {selectedReport && (
              <form>
                <TextField
                  label="Employee ID"
                  value={selectedReport.empId || "N/A"}
                  fullWidth
                  InputProps={{readOnly: true}}
                  margin="normal"
                />
                <TextField
                  label="Employee Name"
                  value={selectedReport.employeeName || "N/A"}
                  fullWidth
                  InputProps={{readOnly: true}}
                  margin="normal"
                />
                <TextField
                  label="Reporting From"
                  value={selectedReport.reportingFrom || "N/A"}
                  fullWidth
                  InputProps={{readOnly: true}}
                  margin="normal"
                />
                <TextField
                  label="Designation"
                  value={selectedReport.designation || "N/A"}
                  fullWidth
                  InputProps={{readOnly: true}}
                  margin="normal"
                />
                <TextField
                  label="Reporting Manager ID"
                  value={selectedReport.reportingManagerID || "N/A"}
                  fullWidth
                  InputProps={{readOnly: true}}
                  margin="normal"
                />
                <TextField
                  label="Reporting Name"
                  value={selectedReport.reportingName || "N/A"}
                  fullWidth
                  InputProps={{readOnly: true}}
                  margin="normal"
                />
              </form>
            )}
          </DialogContent>
        </Dialog>
      </MDBox>
    </DashboardLayout>
  );
};

export default ReportingList;
